/**
 * AddressCorrectionTable - Table view for address correction review queue (desktop)
 *
 * SESSION_352: Admin Review Queue Table Implementation
 *
 * Columns: Status | Business | Current Address | Requested Address | Requested By | Date | Actions
 * Features:
 * - Sortable columns
 * - Inline action buttons (approve/reject)
 * - Modal for approve/reject forms
 * - Address comparison (current strikethrough, requested green)
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Table,
  Badge,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Box,
  Stack
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconArrowUp,
  IconArrowDown
} from '@tabler/icons-react';
import { ReviewActionModal } from '../shared';
import { getStatusColor } from './AddressCorrectionReviewSection.types';
import type { AddressCorrectionListProps } from './AddressCorrectionReviewSection.types';
import type { AddressCorrectionDto } from '@/domain/entities/AddressCorrection';
import { parseAsUTC } from '@/infrastructure/utils/dateUtils';

/** Sort field type */
type SortField = 'status' | 'businessName' | 'currentAddress' | 'requestedAddress' | 'userName' | 'requestedAt';
type SortDirection = 'asc' | 'desc';

/**
 * Format date for table display (compact)
 */
const formatDateCompact = (dateString: string): string => {
  try {
    const date = parseAsUTC(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch {
    return dateString;
  }
};

/**
 * AddressCorrectionTable Props - extends list props but table-specific
 */
export interface AddressCorrectionTableProps extends Omit<AddressCorrectionListProps, 'isLoading' | 'error'> {
  /** Additional className */
  className?: string;
}

/**
 * AddressCorrectionTable Component
 *
 * Displays address corrections in a sortable table format for desktop
 */
export const AddressCorrectionTable: React.FC<AddressCorrectionTableProps> = ({
  corrections,
  onApprove,
  onReject,
  actionsDisabled
}): React.ReactElement => {
  // Sort state
  const [sortField, setSortField] = useState<SortField>('requestedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<'approve' | 'reject'>('approve');
  const [selectedCorrection, setSelectedCorrection] = useState<AddressCorrectionDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Handle sort column click
  const handleSort = useCallback((field: SortField): void => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  // Sort the data
  const sortedCorrections = useMemo(() => {
    return [...corrections].sort((a, b) => {
      let aValue: string | number | null = null;
      let bValue: string | number | null = null;

      switch (sortField) {
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'businessName':
          aValue = a.businessName.toLowerCase();
          bValue = b.businessName.toLowerCase();
          break;
        case 'currentAddress':
          aValue = (a.currentAddress || '').toLowerCase();
          bValue = (b.currentAddress || '').toLowerCase();
          break;
        case 'requestedAddress':
          aValue = a.requestedAddress.toLowerCase();
          bValue = b.requestedAddress.toLowerCase();
          break;
        case 'userName':
          aValue = a.userName.toLowerCase();
          bValue = b.userName.toLowerCase();
          break;
        case 'requestedAt':
          aValue = new Date(a.requestedAt).getTime();
          bValue = new Date(b.requestedAt).getTime();
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = aValue as number;
      const numB = bValue as number;
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  }, [corrections, sortField, sortDirection]);

  // Open action modal
  const openModal = useCallback((correction: AddressCorrectionDto, action: 'approve' | 'reject'): void => {
    setSelectedCorrection(correction);
    setModalAction(action);
    setModalOpen(true);
  }, []);

  // Handle approve submit
  const handleApproveSubmit = useCallback(async (adminNotes?: string): Promise<void> => {
    if (!selectedCorrection) return;
    setIsSubmitting(true);
    try {
      await onApprove(selectedCorrection, adminNotes);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCorrection, onApprove]);

  // Handle reject submit
  const handleRejectSubmit = useCallback(async (rejectionReason: string, adminNotes?: string): Promise<void> => {
    if (!selectedCorrection) return;
    setIsSubmitting(true);
    try {
      await onReject(selectedCorrection, rejectionReason, adminNotes);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCorrection, onReject]);

  // Render sort icon
  const renderSortIcon = (field: SortField): React.ReactElement | null => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <IconArrowUp size={14} style={{ marginLeft: 4 }} />
      : <IconArrowDown size={14} style={{ marginLeft: 4 }} />;
  };

  // Sortable header styles
  const sortableHeaderStyle = { cursor: 'pointer', userSelect: 'none' as const };

  return (
    <>
      <ScrollArea>
        <Box style={{ minWidth: 1000 }}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  onClick={(): void => handleSort('status')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Status</Text>
                    {renderSortIcon('status')}
                  </Group>
                </Table.Th>
                <Table.Th
                  onClick={(): void => handleSort('businessName')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Business</Text>
                    {renderSortIcon('businessName')}
                  </Group>
                </Table.Th>
                <Table.Th
                  onClick={(): void => handleSort('currentAddress')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Current Address</Text>
                    {renderSortIcon('currentAddress')}
                  </Group>
                </Table.Th>
                <Table.Th
                  onClick={(): void => handleSort('requestedAddress')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Requested Address</Text>
                    {renderSortIcon('requestedAddress')}
                  </Group>
                </Table.Th>
                <Table.Th
                  onClick={(): void => handleSort('userName')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Requested By</Text>
                    {renderSortIcon('userName')}
                  </Group>
                </Table.Th>
                <Table.Th
                  onClick={(): void => handleSort('requestedAt')}
                  style={sortableHeaderStyle}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text fw={600} size="sm">Date</Text>
                    {renderSortIcon('requestedAt')}
                  </Group>
                </Table.Th>
                <Table.Th>
                  <Text fw={600} size="sm">Actions</Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedCorrections.map((correction) => {
                const statusColor = getStatusColor(correction.status);
                const isPending = correction.status === 'Pending';

                return (
                  <Table.Tr key={correction.requestId}>
                    {/* Status */}
                    <Table.Td>
                      <Badge color={statusColor} variant="filled" size="sm">
                        {correction.statusDisplay}
                      </Badge>
                    </Table.Td>

                    {/* Business */}
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {correction.businessName}
                      </Text>
                    </Table.Td>

                    {/* Current Address */}
                    <Table.Td style={{ maxWidth: 200 }}>
                      <Text
                        size="sm"
                        td="line-through"
                        c="dimmed"
                        style={{ opacity: 0.7 }}
                        lineClamp={2}
                      >
                        {correction.currentAddress || 'No address on file'}
                      </Text>
                    </Table.Td>

                    {/* Requested Address */}
                    <Table.Td style={{ maxWidth: 200 }}>
                      <Text size="sm" fw={500} c="green" lineClamp={2}>
                        {correction.requestedAddress}
                      </Text>
                    </Table.Td>

                    {/* Requested By */}
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>
                          {correction.userName}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {correction.userEmail}
                        </Text>
                      </Stack>
                    </Table.Td>

                    {/* Date */}
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatDateCompact(correction.requestedAt)}
                      </Text>
                    </Table.Td>

                    {/* Actions */}
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {isPending ? (
                          <>
                            <Tooltip label="Approve">
                              <ActionIcon
                                color="green"
                                variant="light"
                                size="sm"
                                onClick={(): void => openModal(correction, 'approve')}
                                disabled={actionsDisabled}
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Reject">
                              <ActionIcon
                                color="red"
                                variant="light"
                                size="sm"
                                onClick={(): void => openModal(correction, 'reject')}
                                disabled={actionsDisabled}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        ) : (
                          <Text size="xs" c="dimmed">-</Text>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      </ScrollArea>

      {/* Action Modal */}
      <ReviewActionModal
        opened={modalOpen}
        onClose={(): void => setModalOpen(false)}
        actionType={modalAction}
        itemName={selectedCorrection?.businessName || ''}
        onApprove={handleApproveSubmit}
        onReject={handleRejectSubmit}
        isSubmitting={isSubmitting}
        showRejectNotes={true}
        approveConfirmText={selectedCorrection
          ? `Approve address change for ${selectedCorrection.businessName}?\n\nNew address: ${selectedCorrection.requestedAddress}`
          : ''
        }
        rejectConfirmText={selectedCorrection
          ? `Reject address correction request for ${selectedCorrection.businessName}?`
          : ''
        }
      />
    </>
  );
};
